// Searchable combobox: text input + Portal-rendered dropdown of matching
// options. Free-form: typing a value not in the list keeps the typed
// string as the value (used for "kind" — known kinds plus user-coined
// ones). Click an option to pick it; Enter accepts the current input;
// Escape closes the dropdown.

import { For, Show, createMemo, type Accessor } from "solid-js";
import { Portal } from "solid-js/web";
import { usePopupAnchor } from "../../shared/hooks/usePopupAnchor";
import s from "./Combobox.module.css";

export interface ComboboxProps {
  value: Accessor<string>;
  setValue: (v: string) => void;
  options: Accessor<string[]>;
  placeholder?: string;
  id?: string;
}

export function Combobox(props: ComboboxProps) {
  const anchor = usePopupAnchor();
  let inputEl: HTMLInputElement | undefined;

  function openDropdown() { anchor.show(); }
  function closeDropdown() { anchor.hide(); }

  // Filtered options: substring match against the current input value,
  // case-insensitive. Empty input shows all options.
  const filtered = createMemo(() => {
    const q = props.value().trim().toLowerCase();
    if (!q) return props.options();
    return props.options().filter((o) => o.toLowerCase().includes(q));
  });

  function pick(v: string) {
    props.setValue(v);
    closeDropdown();
    inputEl?.focus();
  }

  return (
    <>
      <input
        ref={(el) => { inputEl = el; anchor.bindTrigger(el); }}
        id={props.id}
        type="text"
        class={s.input}
        autocomplete="off"
        placeholder={props.placeholder}
        value={props.value()}
        onFocus={openDropdown}
        onInput={(e) => {
          props.setValue(e.currentTarget.value);
          if (!anchor.open()) openDropdown();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            closeDropdown();
          } else if (e.key === "Enter") {
            // If the input matches an option exactly, pick it; otherwise
            // keep the free-form value the user typed. Either way close
            // the dropdown but DON'T submit the form (form submit goes
            // through the explicit primary button).
            e.preventDefault();
            const opts = props.options();
            const exact = opts.find((o) => o.toLowerCase() === props.value().trim().toLowerCase());
            if (exact) props.setValue(exact);
            closeDropdown();
          } else if (e.key === "ArrowDown" && !anchor.open()) {
            openDropdown();
          }
        }}
      />
      <Show when={anchor.open() && anchor.pos()}>
        <Portal>
          <div
            ref={(el) => anchor.bindPopup(el)}
            class={s.popup}
            style={{
              position: "fixed",
              top: `${anchor.pos()!.top}px`,
              left: `${anchor.pos()!.left}px`,
              width: `${anchor.pos()!.width}px`,
            }}
          >
            <Show
              when={filtered().length > 0}
              fallback={
                <div class={s.empty}>
                  <Show
                    when={props.value().trim()}
                    fallback={<>No options</>}
                  >
                    Press Enter to use “{props.value().trim()}”
                  </Show>
                </div>
              }
            >
              <For each={filtered()}>
                {(opt) => (
                  <div
                    class={`${s.option} ${opt === props.value() ? s.optionActive : ""}`}
                    onMouseDown={(e) => { e.preventDefault(); pick(opt); }}
                  >
                    {opt}
                  </div>
                )}
              </For>
            </Show>
          </div>
        </Portal>
      </Show>
    </>
  );
}
